import { useCallback } from 'react';
import type { Pedido } from '../../../contexts/OrderContext';
import { storage } from '../../../lib/storage/storage';
import { Keys } from '../../../lib/storage/keys';
import { clearPreparedFlag } from '../../../lib/corel/pipeline/pipelineKeys';
import { terminalLog } from '../../../lib/terminalLogger';

interface UseOrderUtilityHandlersParams {
  onClose: () => void;
  onNavigateToMain?: () => void;
  monitorParar: () => Promise<void> | void;
  selectedPedido: Pedido | null;
  selectedItem: any | null;
  role: string;
  isRefreshing: boolean;
  setIsRefreshing: (value: boolean) => void;
  isSyncing: boolean;
  setIsSyncing: (value: boolean) => void;
  syncAllWithCorel: () => Promise<any>;
  layerOrganizador: () => string | null | undefined;
}

export function useOrderUtilityHandlers({
  onClose,
  onNavigateToMain,
  monitorParar,
  selectedPedido,
  selectedItem,
  role,
  isRefreshing,
  setIsRefreshing,
  isSyncing,
  setIsSyncing,
  syncAllWithCorel,
  layerOrganizador,
}: UseOrderUtilityHandlersParams) {
  const handleCloseAll = useCallback(() => {
    try { monitorParar(); } catch {}
    onClose();
  }, [monitorParar, onClose]);

  const handleExportComplete = useCallback(() => {
    if (onNavigateToMain) {
      onNavigateToMain();
    }
  }, [onNavigateToMain]);

  const handleRefreshObjects = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      if (role === 'producao') {
        await syncAllWithCorel();
      } else {
        const requestId = layerOrganizador();
        if (requestId) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    } catch {
      // silencioso
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, layerOrganizador, role, setIsRefreshing, syncAllWithCorel]);

  const handleSyncObjects = useCallback(async () => {
    if (isSyncing) return;

    setIsSyncing(true);

    try {
      await syncAllWithCorel();
    } catch {
      // silencioso
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, setIsSyncing, syncAllWithCorel]);

  const cleanupExportStorage = useCallback(() => {
    try {
      try { storage.remove(Keys.export.currentSaveData, 'session'); } catch {}
      try { storage.remove(Keys.export.currentSaveData, 'local'); } catch {}
      try { sessionStorage.removeItem('currentSaveData'); } catch {}
      try { localStorage.removeItem('currentSaveData'); } catch {}

      try { storage.remove(Keys.export.selectedItemId, 'session'); } catch {}

      try { storage.remove(Keys.export.initialMetadataSnapshot, 'session'); } catch {}
      try { storage.remove(Keys.export.lastMetadataSnapshot, 'session'); } catch {}

      try { storage.remove(Keys.export.lastSaveRequestId, 'session'); } catch {}
      try { localStorage.removeItem('lastSaveRequestId'); } catch {}
      try { localStorage.removeItem('lastSaveRequest'); } catch {}

      const pid = selectedPedido?.orc_id;
      const iid = selectedItem?.oit_id;
      if (pid && iid) {
        try { sessionStorage.removeItem(`corel:open:v1:${pid}:${iid}`); } catch {}
        try { sessionStorage.removeItem(`corel:dist:v1:${pid}:${iid}`); } catch {}
        // Usar helper para limpar pipeline preparada
        try {
          clearPreparedFlag(pid, iid);
        } catch {}
      }
    } catch (error) {
      terminalLog.warn('[ORDER_UTILITY] Falha ao limpar storage pós-salvamento', { 
        error: error instanceof Error ? error.message : String(error) 
      });
    }
  }, [selectedItem?.oit_id, selectedPedido?.orc_id]);

  return {
    handleCloseAll,
    handleExportComplete,
    handleRefreshObjects,
    handleSyncObjects,
    cleanupExportStorage,
  };
}

